import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Palette, Globe, Building, Save } from 'lucide-react';

import { authHeaders } from '../lib/authToken';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * White-label Configuration 
 * Multi-tenant ready: branding, couleurs, domaine, paramètres
 */
export default function AdminWhiteLabel() {
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('branding');

  useEffect(() => {
    fetch(`${BACKEND}/api/parametres`)
      .then(r => r.json())
      .then(d => { setSettings(d); setPreview(d); })
      .catch((err) => { /* eslint-disable-line no-empty */ });
  }, []);

  async function saveSettings() {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND}/api/parametres`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(settings)
      });
      if (res.ok) {
        toast.success('White-label sauvegardé !');
        setPreview({ ...settings });
      }
    } catch (e) { toast.error('Erreur sauvegarde'); }
    setSaving(false);
  }

  if (!settings) return <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 rounded-full" style={{ border: '2px solid #2A2A2A', borderTopColor: '#D4AF37' }} /></div>;

  const TABS = [
    { id: 'branding', label: 'Marque' },
    { id: 'couleurs', label: 'Couleurs' },
    { id: 'contact', label: 'Contact & Zone' },
    { id: 'preview', label: 'Aperçu' },
  ];

  return (
    <div data-testid="admin-page">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Paramètres</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>White-label</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Configuration marque blanche SaaS</p>
      </div>
      <div className="max-w-2xl">
      <div className="mb-6">
        <h2 className="font-display text-xl font-bold" style={{ color: 'var(--admin-text)' }}>White-label & Marque</h2>
        <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>Personnalisez l'éditeur selon votre marque (multi-tenant Show Événement)</p>
      </div>

      <div className="flex gap-1 mb-6 p-1 rounded-xl" style={{ background: 'var(--admin-surface)' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className="flex-1 py-2 rounded-lg text-xs font-medium transition-all"
            style={{ background: activeTab === t.id ? '#D4AF37' : 'transparent', color: activeTab === t.id ? '#0D0D0D' : 'rgba(255,255,255,0.5)' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* BRANDING */}
      {activeTab === 'branding' && (
        <div className="p-5 rounded-xl space-y-4" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
          <h3 className="font-semibold text-sm" style={{ color: 'var(--admin-gold)' }}>Identité de la marque</h3>
          {[
            { key: 'nom_entreprise', label: 'Nom entreprise', placeholder: 'Show Événement' },
            { key: 'logo_url', label: 'URL du logo', placeholder: 'https://...' },
            { key: 'slogan', label: 'Slogan / Tagline', placeholder: 'Votre événement simplement' },
          ].map(f => (
            <div key={f.key}>
              <label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>{f.label}</label>
              <input type="text" value={settings[f.key] || ''}
                onChange={e => setSettings(p => ({ ...p, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                className="w-full px-3 py-2.5 rounded-xl text-sm"
                style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} />
            </div>
          ))}
          {settings.logo_url && (
            <div>
              <p className="text-xs mb-2" style={{ color: 'var(--admin-muted)' }}>Aperçu logo :</p>
              <img src={settings.logo_url} alt="Logo" className="h-12 rounded-lg" style={{ background: '#2A2A2A', padding: '4px' }} loading="lazy" />
            </div>
          )}
        </div>
      )}

      {/* COULEURS */}
      {activeTab === 'couleurs' && (
        <div className="p-5 rounded-xl space-y-4" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
          <h3 className="font-semibold text-sm" style={{ color: 'var(--admin-gold)' }}>Palette de couleurs</h3>
          <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>Ces couleurs seront utilisées dans les emails, PDFs et futurs thèmes white-label.</p>
          {[
            { key: 'couleur_primaire', label: 'Couleur primaire (or/CTA)', default: '#D4AF37' },
            { key: 'couleur_secondaire', label: 'Couleur secondaire', default: '#C5D8F9' },
            { key: 'couleur_fond', label: 'Couleur de fond', default: '#0D0D0D' },
            { key: 'couleur_accent', label: 'Couleur accent', default: '#F9C5D1' },
          ].map(f => (
            <div key={f.key} className="flex items-center gap-4">
              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>{f.label}</label>
                <div className="flex items-center gap-3">
                  <input type="color"
                    value={settings[f.key] || f.default}
                    onChange={e => setSettings(p => ({ ...p, [f.key]: e.target.value }))}
                    className="w-12 h-10 rounded-lg cursor-pointer border-0"
                  />
                  <span className="font-mono-ose text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    {settings[f.key] || f.default}
                  </span>
                </div>
              </div>
              {/* Color preview */}
              <div className="w-8 h-8 rounded-lg flex-shrink-0" style={{ background: settings[f.key] || f.default }} />
            </div>
          ))}
          {/* Preview palette */}
          <div>
            <p className="text-xs mb-2" style={{ color: 'var(--admin-muted)' }}>Aperçu palette :</p>
            <div className="flex gap-2">
              {['couleur_primaire', 'couleur_secondaire', 'couleur_accent'].map(k => (
                <div key={k} className="flex-1 h-10 rounded-xl" style={{ background: settings[k] || '#D4AF37' }} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CONTACT & ZONE */}
      {activeTab === 'contact' && (
        <div className="p-5 rounded-xl space-y-4" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
          <h3 className="font-semibold text-sm" style={{ color: 'var(--admin-gold)' }}>Informations de contact</h3>
          {[
            { key: 'email_contact', label: 'Email contact', type: 'email' },
            { key: 'telephone', label: 'Téléphone', type: 'tel' },
            { key: 'adresse', label: 'Adresse', type: 'text' },
            { key: 'google_review_url', label: 'Lien avis Google', type: 'url' },
          ].map(f => (
            <div key={f.key}>
              <label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>{f.label}</label>
              <input type={f.type} value={settings[f.key] || ''}
                onChange={e => setSettings(p => ({ ...p, [f.key]: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl text-sm"
                style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} />
            </div>
          ))}
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>Zones desservies (une par ligne)</label>
            <textarea
              rows={5}
              value={(settings.zones_desservies || []).join('\n')}
              onChange={e => setSettings(p => ({ ...p, zones_desservies: e.target.value.split('\n').filter(z => z.trim()) }))}
              className="w-full px-3 py-2.5 rounded-xl text-sm resize-none"
              style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }}
            />
          </div>
        </div>
      )}

      {/* PREVIEW */}
      {activeTab === 'preview' && (
        <div className="p-5 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
          <h3 className="font-semibold text-sm mb-4" style={{ color: 'var(--admin-gold)' }}>Aperçu white-label</h3>
          {/* Mini hero preview */}
          <div className="rounded-xl overflow-hidden" style={{ background: settings.couleur_fond || '#0D0D0D', padding: '24px', border: '1px solid #2A2A2A' }}>
            <div className="flex items-center gap-2 mb-4">
              {settings.logo_url
                ? <img src={settings.logo_url} alt="Logo" className="h-8" loading="lazy" />
                : <div className="w-8 h-8 rounded-lg" style={{ background: settings.couleur_primaire || '#D4AF37' }} />}
              <span className="font-bold" style={{ color: settings.couleur_primaire || '#D4AF37' }}>
                {settings.nom_entreprise || 'Show Événement'}
              </span>
            </div>
            <h2 style={{ color: 'var(--admin-text)', fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
              Créez votre événement simplement
            </h2>
            <button style={{
              background: settings.couleur_primaire || '#D4AF37',
              color: '#0D0D0D',
              padding: '10px 20px',
              borderRadius: '10px',
              fontWeight: 'bold',
              fontSize: '14px',
              border: 'none',
              cursor: 'pointer'
            }}>
              Créer mon événement
            </button>
            <p style={{ color: 'var(--admin-muted)', fontSize: '11px', marginTop: '16px' }}>
              {settings.adresse} — {settings.email_contact}
            </p>
          </div>
          <p className="text-xs mt-3" style={{ color: 'var(--admin-faint)' }}>Conçu par OSEZ STUDIO, Groupe Market & Développement</p>
        </div>
      )}

      <button onClick={saveSettings} disabled={saving}
        className="btn-gold w-full py-3 rounded-xl text-sm font-semibold mt-4 flex items-center gap-2 justify-center disabled:opacity-50">
        <Save size={16} />
        {saving ? 'Sauvegarde...' : 'Enregistrer la configuration white-label'}
      </button>
      </div>
    </div>
  );
}
